import React, { useEffect, useRef, useState } from 'react';
import { Bot, Sparkles, ArrowRight, RefreshCw, TrendingUp, Zap, MessageCircle } from 'lucide-react';

/**
 * AI Agent Focus Section — sits directly under the Hero.
 * Maximum spotlight for the AI Agent: Bestandsbearbeitung,
 * Bestandsaufbau, Bestandsreaktivierung, Lead-Kommunikation.
 */
const USE_CASES = [
  {
    id: 'bearbeitung',
    icon: RefreshCw,
    title: 'Bestandsbearbeitung',
    desc: 'Automatische Klassifizierung, Wiedervorlagen & Reminders — kein Lead geht mehr verloren.',
    color: '#00FFD1',
  },
  {
    id: 'aufbau',
    icon: TrendingUp,
    title: 'Bestandsaufbau',
    desc: 'Warmes Cross- & Upselling: KI erkennt Potenzial im Bestand und meldet sich für dich.',
    color: '#00D4FF',
  },
  {
    id: 'reaktivierung',
    icon: Zap,
    title: 'Bestandsreaktivierung',
    desc: 'Schlummernde Kontakte automatisiert wecken — 24/7 per WhatsApp, ohne Vertriebsdruck.',
    color: '#A855F7',
  },
  {
    id: 'kommunikation',
    icon: MessageCircle,
    title: 'Lead-Kommunikation',
    desc: 'Sofort-Antwort auf jeden neuen Lead — qualifiziert, terminiert, gebucht. Vollautomatisch.',
    color: '#EC4899',
  },
];

const AIAgentFocusSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const openChat = () => {
    window.dispatchEvent(new CustomEvent('open-qualify-chat'));
  };

  return (
    <section
      ref={ref}
      className={`ai-focus-section ${isVisible ? 'is-in' : ''}`}
      data-testid="ai-agent-focus-section"
      id="ai-agent"
    >
      {/* Ambient background aurora */}
      <div className="ai-focus-aurora ai-focus-aurora--a" aria-hidden="true" />
      <div className="ai-focus-aurora ai-focus-aurora--b" aria-hidden="true" />
      <div className="ai-focus-aurora ai-focus-aurora--c" aria-hidden="true" />

      <div className="ai-focus-container">
        {/* Main glass card */}
        <div className="ai-focus-card">
          {/* Rotating conic border */}
          <div className="ai-focus-card-border" aria-hidden="true" />

          <div className="ai-focus-card-inner">
            {/* Header row */}
            <div className="ai-focus-badge-row">
              <span className="ai-focus-live-badge" data-testid="ai-focus-live-badge">
                <span className="ai-focus-live-dot" />
                LIVE · 24/7 aktiv
              </span>
              <span className="ai-focus-eyebrow">
                <Sparkles size={13} strokeWidth={2.6} />
                DEIN KI AGENT — Maximum Focus
              </span>
            </div>

            {/* Icon + Title stack */}
            <div className="ai-focus-hero-row">
              <div className="ai-focus-icon-wrap">
                <div className="ai-focus-icon-halo" aria-hidden="true" />
                <div className="ai-focus-icon">
                  <Bot size={44} strokeWidth={2} />
                </div>
              </div>

              <div className="ai-focus-hero-copy">
                <h2 className="ai-focus-title">
                  Ein KI Agent, der deinen<br />
                  <span className="ai-focus-title-gradient">gesamten Bestand aktiviert.</span>
                </h2>
                <p className="ai-focus-sub">
                  Bestandsbearbeitung, Bestandsaufbau, Bestandsreaktivierung und Lead-Kommunikation —
                  <strong> alles in einem KI-Agenten.</strong> Rund um die Uhr. In deinem Namen.
                  Auf WhatsApp, E-Mail und im CRM.
                </p>
              </div>
            </div>

            {/* CTA row */}
            <div className="ai-focus-cta-row">
              <button
                type="button"
                onClick={openChat}
                className="ai-focus-cta"
                data-testid="ai-focus-cta"
                aria-label="Jetzt mit dem KI Agenten sprechen"
              >
                <span className="ai-focus-cta-icon">
                  <Bot size={18} strokeWidth={2.4} />
                </span>
                <span className="ai-focus-cta-text">
                  Jetzt mit dem KI Agenten sprechen
                </span>
                <span className="ai-focus-cta-arrow">
                  <ArrowRight size={18} strokeWidth={2.6} />
                </span>
              </button>
              <div className="ai-focus-cta-meta">
                <span className="ai-focus-cta-dot" />
                Antwortet in unter 3 Sekunden · qualifiziert dich in 3 Fragen
              </div>
            </div>

            {/* 4 use case pillars */}
            <div className="ai-focus-grid">
              {USE_CASES.map((u, i) => {
                const Icon = u.icon;
                return (
                  <div
                    key={u.id}
                    className="ai-focus-pillar"
                    style={{
                      '--pillar-acc': u.color,
                      transitionDelay: `${0.15 + i * 0.08}s`,
                    }}
                    data-testid={`ai-focus-pillar-${u.id}`}
                  >
                    <div className="ai-focus-pillar-icon">
                      <Icon size={20} strokeWidth={2.2} />
                    </div>
                    <div className="ai-focus-pillar-title">{u.title}</div>
                    <div className="ai-focus-pillar-desc">{u.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ai-focus-section {
          position: relative;
          padding: 60px 24px 80px;
          background: transparent;
          overflow: hidden;
          isolation: isolate;
        }

        /* Aurora blobs */
        .ai-focus-aurora {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
          z-index: 0;
        }
        .ai-focus-aurora--a {
          top: -80px; left: -80px;
          width: 480px; height: 480px;
          background: radial-gradient(circle, rgba(0, 255, 209, 0.28), transparent 70%);
          animation: ai-focus-drift-a 18s ease-in-out infinite;
        }
        .ai-focus-aurora--b {
          bottom: -100px; right: -80px;
          width: 520px; height: 520px;
          background: radial-gradient(circle, rgba(124, 58, 237, 0.28), transparent 70%);
          animation: ai-focus-drift-b 22s ease-in-out infinite;
        }
        .ai-focus-aurora--c {
          top: 30%; left: 40%;
          width: 380px; height: 380px;
          background: radial-gradient(circle, rgba(0, 212, 255, 0.18), transparent 70%);
          animation: ai-focus-drift-c 26s ease-in-out infinite;
        }
        @keyframes ai-focus-drift-a {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(40px, 40px); }
        }
        @keyframes ai-focus-drift-b {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(-40px, -30px); }
        }
        @keyframes ai-focus-drift-c {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50%      { transform: translate(-45%, -55%) scale(1.08); }
        }

        .ai-focus-container {
          position: relative;
          z-index: 2;
          max-width: 1180px;
          margin: 0 auto;
        }

        /* Main glass card */
        .ai-focus-card {
          position: relative;
          border-radius: 32px;
          padding: 2px;
          background: linear-gradient(135deg,
            rgba(0, 255, 209, 0.55) 0%,
            rgba(0, 212, 255, 0.35) 35%,
            rgba(168, 85, 247, 0.55) 70%,
            rgba(236, 72, 153, 0.35) 100%);
          box-shadow:
            0 0 0 1px rgba(255, 255, 255, 0.02),
            0 30px 80px rgba(0, 0, 0, 0.55),
            0 0 60px rgba(0, 255, 209, 0.18),
            0 0 100px rgba(168, 85, 247, 0.15);
          opacity: 0;
          transform: translateY(30px);
          transition: opacity .9s cubic-bezier(.22,1,.36,1),
                      transform .9s cubic-bezier(.22,1,.36,1);
        }
        .ai-focus-section.is-in .ai-focus-card {
          opacity: 1;
          transform: translateY(0);
        }

        .ai-focus-card-border {
          position: absolute;
          inset: -1px;
          border-radius: 32px;
          background: conic-gradient(
            from 0deg,
            rgba(0, 255, 209, 0.9),
            rgba(0, 212, 255, 0.2),
            rgba(168, 85, 247, 0.9),
            rgba(0, 212, 255, 0.2),
            rgba(0, 255, 209, 0.9)
          );
          opacity: 0.45;
          filter: blur(6px);
          animation: ai-focus-rotate 8s linear infinite;
          z-index: -1;
        }
        @keyframes ai-focus-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        .ai-focus-card-inner {
          position: relative;
          border-radius: 30px;
          padding: 44px 48px 40px;
          background:
            linear-gradient(180deg, rgba(12, 15, 26, 0.94), rgba(8, 10, 20, 0.96));
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          overflow: hidden;
        }

        /* Header row */
        .ai-focus-badge-row {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
          margin-bottom: 22px;
        }
        .ai-focus-live-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 12px 6px 10px;
          border-radius: 999px;
          background: rgba(0, 255, 209, 0.08);
          border: 1px solid rgba(0, 255, 209, 0.35);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #00FFD1;
        }
        .ai-focus-live-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #00FFD1;
          box-shadow: 0 0 10px #00FFD1;
          animation: ai-focus-pulse 1.6s ease-in-out infinite;
        }
        @keyframes ai-focus-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.45; transform: scale(1.4); }
        }
        .ai-focus-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.55);
        }
        .ai-focus-eyebrow svg { color: #00D4FF; }

        /* Hero row */
        .ai-focus-hero-row {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 26px;
          align-items: flex-start;
          margin-bottom: 28px;
        }
        .ai-focus-icon-wrap {
          position: relative;
          width: 96px;
          height: 96px;
          flex-shrink: 0;
        }
        .ai-focus-icon-halo {
          position: absolute;
          inset: -14px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0, 255, 209, 0.4), transparent 70%);
          filter: blur(12px);
          animation: ai-focus-halo 3s ease-in-out infinite;
        }
        @keyframes ai-focus-halo {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.1); }
        }
        .ai-focus-icon {
          position: relative;
          width: 96px; height: 96px;
          border-radius: 24px;
          background:
            linear-gradient(135deg, rgba(0, 255, 209, 0.15), rgba(168, 85, 247, 0.15)) padding-box,
            linear-gradient(135deg, #00FFD1, #A855F7) border-box;
          border: 1.5px solid transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #00FFD1;
          box-shadow:
            0 12px 30px rgba(0, 0, 0, 0.5),
            inset 0 0 24px rgba(0, 255, 209, 0.1);
        }
        .ai-focus-title {
          font-size: clamp(30px, 4vw, 48px);
          line-height: 1.08;
          font-weight: 800;
          letter-spacing: -0.02em;
          margin: 0 0 14px;
          color: #FFFFFF;
        }
        .ai-focus-title-gradient {
          background: linear-gradient(135deg, #00FFD1 0%, #00D4FF 45%, #A855F7 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .ai-focus-sub {
          font-size: clamp(15px, 1.5vw, 17.5px);
          line-height: 1.65;
          color: rgba(255,255,255,0.78);
          margin: 0;
          max-width: 720px;
        }
        .ai-focus-sub strong {
          color: #FFFFFF;
          font-weight: 700;
        }

        /* CTA row */
        .ai-focus-cta-row {
          display: flex;
          align-items: center;
          gap: 22px;
          flex-wrap: wrap;
          margin-bottom: 36px;
        }
        .ai-focus-cta {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 16px 26px 16px 22px;
          border: none;
          border-radius: 999px;
          background: linear-gradient(135deg, #00FFD1 0%, #00D4FF 55%, #A855F7 130%);
          background-size: 200% 100%;
          background-position: 0% 50%;
          color: #061014;
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 0.02em;
          cursor: pointer;
          overflow: hidden;
          isolation: isolate;
          box-shadow:
            0 0 0 1.5px rgba(0, 255, 209, 0.5),
            0 0 28px rgba(0, 255, 209, 0.45),
            0 16px 40px rgba(0, 212, 255, 0.32);
          animation: ai-focus-cta-pulse 3s ease-in-out infinite;
          transition: transform .35s cubic-bezier(.22,1,.36,1),
                      background-position .6s ease,
                      box-shadow .35s ease;
        }
        .ai-focus-cta:hover {
          transform: translateY(-3px);
          background-position: 100% 50%;
          box-shadow:
            0 0 0 1.5px rgba(0, 255, 209, 0.8),
            0 0 44px rgba(0, 255, 209, 0.85),
            0 24px 56px rgba(168, 85, 247, 0.5);
        }
        .ai-focus-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%);
          transform: translateX(-100%);
          transition: transform 0.9s cubic-bezier(.22,1,.36,1);
          z-index: 1;
          pointer-events: none;
        }
        .ai-focus-cta:hover::before { transform: translateX(100%); }
        .ai-focus-cta-icon,
        .ai-focus-cta-text,
        .ai-focus-cta-arrow { position: relative; z-index: 2; }
        .ai-focus-cta-icon {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; height: 30px;
          border-radius: 50%;
          background: rgba(6, 16, 20, 0.15);
        }
        .ai-focus-cta-arrow {
          display: inline-flex; align-items: center; justify-content: center;
          transition: transform .3s cubic-bezier(.22,1,.36,1);
        }
        .ai-focus-cta:hover .ai-focus-cta-arrow { transform: translateX(5px); }
        @keyframes ai-focus-cta-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1.5px rgba(0, 255, 209, 0.45),
              0 0 24px rgba(0, 255, 209, 0.38),
              0 16px 40px rgba(0, 212, 255, 0.28);
          }
          50% {
            box-shadow:
              0 0 0 1.5px rgba(0, 255, 209, 0.75),
              0 0 40px rgba(0, 255, 209, 0.75),
              0 16px 40px rgba(168, 85, 247, 0.5);
          }
        }

        .ai-focus-cta-meta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.68);
          letter-spacing: 0.01em;
        }
        .ai-focus-cta-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #00FFD1;
          box-shadow: 0 0 10px #00FFD1;
          animation: ai-focus-pulse 1.8s ease-in-out infinite;
        }

        /* Pillars grid */
        .ai-focus-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
        }
        .ai-focus-pillar {
          padding: 20px 18px;
          border-radius: 18px;
          background:
            linear-gradient(180deg, rgba(20, 24, 38, 0.75), rgba(12, 15, 26, 0.75)) padding-box,
            linear-gradient(135deg, color-mix(in srgb, var(--pillar-acc) 50%, rgba(255,255,255,0.05)), rgba(255,255,255,0.04)) border-box;
          border: 1px solid transparent;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity .7s cubic-bezier(.22,1,.36,1),
                      transform .7s cubic-bezier(.22,1,.36,1),
                      border-color .3s ease,
                      box-shadow .3s ease;
        }
        .ai-focus-section.is-in .ai-focus-pillar {
          opacity: 1;
          transform: translateY(0);
        }
        .ai-focus-pillar:hover {
          box-shadow:
            0 16px 36px rgba(0, 0, 0, 0.4),
            0 0 24px color-mix(in srgb, var(--pillar-acc) 30%, transparent);
        }
        .ai-focus-pillar-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px; height: 40px;
          border-radius: 12px;
          background: color-mix(in srgb, var(--pillar-acc) 14%, rgba(255,255,255,0.03));
          border: 1px solid color-mix(in srgb, var(--pillar-acc) 40%, transparent);
          color: var(--pillar-acc);
          margin-bottom: 12px;
        }
        .ai-focus-pillar-title {
          font-size: 15.5px;
          font-weight: 800;
          color: #FFFFFF;
          margin-bottom: 6px;
          letter-spacing: -0.01em;
        }
        .ai-focus-pillar-desc {
          font-size: 13px;
          line-height: 1.55;
          color: rgba(255,255,255,0.62);
        }

        /* Responsive */
        @media (max-width: 900px) {
          .ai-focus-card-inner { padding: 32px 26px 28px; }
          .ai-focus-hero-row { grid-template-columns: 1fr; gap: 18px; }
          .ai-focus-icon-wrap { width: 76px; height: 76px; }
          .ai-focus-icon { width: 76px; height: 76px; border-radius: 20px; }
          .ai-focus-icon svg { width: 34px; height: 34px; }
          .ai-focus-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 560px) {
          .ai-focus-section { padding: 44px 16px 60px; }
          .ai-focus-card-inner { padding: 26px 20px; }
          .ai-focus-grid { grid-template-columns: 1fr; }
          .ai-focus-cta { font-size: 14.5px; padding: 14px 20px 14px 16px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ai-focus-card,
          .ai-focus-pillar { transition: none; opacity: 1; transform: none; }
          .ai-focus-aurora--a,
          .ai-focus-aurora--b,
          .ai-focus-aurora--c,
          .ai-focus-icon-halo,
          .ai-focus-live-dot,
          .ai-focus-cta-dot,
          .ai-focus-cta,
          .ai-focus-card-border { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default AIAgentFocusSection;
